"use client";

import { ChevronDown } from "lucide-react";
import { useState } from "react";
import type { ReactElement } from "react";
import type { CareersProcessCopy } from "./careers-content";
import { CAREERS_CONTENT } from "./careers-content";
import { useCareersLanguage } from "./careers-language-context";

export interface CareersProcessSectionProps {
  readonly process?: CareersProcessCopy;
}

/** Application process accordion (elevated cards, accent number tiles).
 * Steps toggle independently. Optional `process` overrides careers copy. */
export function CareersProcessSection({
  process,
}: CareersProcessSectionProps = {}): ReactElement {
  const { lang } = useCareersLanguage();
  const t: CareersProcessCopy = process ?? CAREERS_CONTENT[lang].process;
  const [openIndexes, setOpenIndexes] = useState<ReadonlySet<number>>(
    new Set(),
  );

  const toggle = (index: number): void => {
    setOpenIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <section id="process" className="dvf-section dvf-section-alt c2-center">
      <div className="dvf-section-inner">
        <h2 className="dvf-section-title">{t.title}</h2>
        <p className="dvf-section-intro">{t.subtitle}</p>

        <div className="c2-acc">
          {t.steps.map((step, index) => {
            const open = openIndexes.has(index);
            return (
              <div className="c2-acc-item" key={step.title}>
                <button
                  type="button"
                  className="c2-acc-trigger"
                  onClick={() => toggle(index)}
                  aria-expanded={open}
                >
                  <span className="c2-acc-num">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="c2-acc-title">{step.title}</span>
                  <span
                    className={
                      open
                        ? "c2-acc-chevron c2-acc-chevron-open"
                        : "c2-acc-chevron"
                    }
                  >
                    <ChevronDown
                      size={16}
                      strokeWidth={2.2}
                      aria-hidden={true}
                    />
                  </span>
                </button>
                <div
                  className={
                    open
                      ? "c2-acc-body-wrap c2-acc-body-open"
                      : "c2-acc-body-wrap"
                  }
                >
                  <div className="c2-acc-body-clip">
                    <p className="c2-acc-body">{step.body}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
